// ─────────────────────────────────────────────────────────────────────────────
// EVAL SIMULATOR
//
// Given a list of trades and a prop firm's ruleset, simulates whether the
// account would survive and pass the eval. Used by EvalSurvival page to
// compute "what's my pass rate against this firm?"
//
// Pure functions only — no React, no Supabase, no DOM. Can be tested via
// Node by importing this file directly.
//
// CONVENTIONS:
//   - trade.pnl is GROSS (matches broker statement, what we've been storing)
//   - net P&L per trade = pnl - fees - commissions (this is what hits balance)
//   - trade_date is the CME session day (YYYY-MM-DD string)
//   - entry_time is HH:MM:SS for ordering within a day
// ─────────────────────────────────────────────────────────────────────────────

// Net P&L impact of one trade on the account balance.
function tradeNet(t) {
  const gross = Number(t.pnl)         || 0;
  const fees  = Number(t.fees)        || 0;
  const comm  = Number(t.commissions) || 0;
  return gross - fees - comm;
}

// Sort trades chronologically by (trade_date, entry_time).
function sortTrades(trades) {
  return [...trades].sort((a, b) => {
    const da = String(a.trade_date || "");
    const db = String(b.trade_date || "");
    if (da !== db) return da.localeCompare(db);
    const ta = String(a.entry_time || "00:00:00");
    const tb = String(b.entry_time || "00:00:00");
    return ta.localeCompare(tb);
  });
}

// Group trades by trade_date (session day). Returns Map<string, Trade[]>.
function groupByDay(trades) {
  const map = new Map();
  for (const t of trades) {
    const d = String(t.trade_date || "").slice(0, 10);
    if (!d) continue;
    if (!map.has(d)) map.set(d, []);
    map.get(d).push(t);
  }
  // Sort each day's trades by entry_time
  for (const day of map.values()) {
    day.sort((a, b) => String(a.entry_time || "").localeCompare(String(b.entry_time || "")));
  }
  return map;
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN SIMULATOR
//
// Runs ONE simulated eval over a given trade set. Returns the outcome.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Simulate a single eval run.
 *
 * @param {Array} trades  - array of trade objects (gross pnl, fees, commissions, trade_date, entry_time, direction)
 * @param {Object} rules  - prop firm preset (see prop_firm_presets schema)
 * @returns {Object} - result with passed/failed flag and breakdown
 */
export function simulateEval(trades, rules) {
  const state = {
    rules,
    balance: Number(rules.starting_balance),
    high_water_mark: Number(rules.starting_balance),
    alive: true,
    passed: false,
    failure_reason: null,
    failure_date: null,
    failure_detail: null,
    trading_days: [],         // ordered list of session days that had trades
    daily_pnl: {},            // session_day -> net P&L for that day
    daily_min_intraday: {},   // session_day -> worst intraday running P&L
    halted_days: [],          // session days where soft-DLL stopped trading
    closest_calls: [],        // narrowly-avoided breaches
    days_traded: 0,
  };

  const startBal     = Number(rules.starting_balance);
  const profitTarget = rules.profit_target != null ? Number(rules.profit_target) : null;
  const maxDD        = Number(rules.max_drawdown);
  const ddType       = rules.drawdown_type;
  const maxDLL       = rules.max_daily_loss != null ? Number(rules.max_daily_loss) : null;
  const dllType      = rules.daily_loss_type || "hard";

  const byDay = groupByDay(trades);
  const sortedDays = [...byDay.keys()].sort();

  for (const date of sortedDays) {
    if (!state.alive) break;

    const dayTrades = byDay.get(date);
    let intradayRunning = 0;
    let intradayMin = 0;
    let dayNetPnl = 0;
    let halted = false;

    for (const t of dayTrades) {
      // If a soft-DLL was hit earlier today, skip remaining trades for this day.
      if (halted) continue;

      const net = tradeNet(t);
      intradayRunning += net;
      intradayMin = Math.min(intradayMin, intradayRunning);
      dayNetPnl += net;
      state.balance += net;

      // INTRADAY drawdown check (for firms using trailing_intraday)
      if (ddType === "trailing_intraday") {
        if (state.balance > state.high_water_mark) {
          state.high_water_mark = state.balance;
        }
        const dd = state.high_water_mark - state.balance;
        if (dd > maxDD) {
          state.alive = false;
          state.failure_reason = "trailing_drawdown";
          state.failure_date = date;
          state.failure_detail = `Trailing drawdown breached intraday: $${dd.toFixed(2)} > limit $${maxDD}`;
          break;
        }
      }

      // INTRADAY daily loss check
      if (maxDLL != null && intradayRunning < -maxDLL) {
        if (dllType === "hard") {
          state.alive = false;
          state.failure_reason = "daily_loss";
          state.failure_date = date;
          state.failure_detail = `Daily loss hit intraday: $${Math.abs(intradayRunning).toFixed(2)} > limit $${maxDLL}`;
          break;
        } else if (dllType === "soft") {
          // Halt today only, account survives
          halted = true;
          state.halted_days.push(date);
          // Note: dayNetPnl freezes here (no more trades counted)
          // But the breach amount is preserved as the day's PnL
          break;
        }
      }
    }

    if (!state.alive) break;

    // END-OF-DAY checks
    state.trading_days.push(date);
    state.daily_pnl[date] = dayNetPnl;
    state.daily_min_intraday[date] = intradayMin;
    state.days_traded += 1;

    // EOD trailing drawdown
    if (ddType === "trailing_eod") {
      if (state.balance > state.high_water_mark) {
        state.high_water_mark = state.balance;
      }
      const dd = state.high_water_mark - state.balance;
      if (dd > maxDD) {
        state.alive = false;
        state.failure_reason = "trailing_drawdown";
        state.failure_date = date;
        state.failure_detail = `Trailing drawdown breached EOD: $${dd.toFixed(2)} > limit $${maxDD}`;
        break;
      }
      // Track close calls (within 10% of the breach)
      if (maxDD - dd > 0 && maxDD - dd < maxDD * 0.10) {
        state.closest_calls.push({
          date, type: "trailing_dd", margin: maxDD - dd,
          detail: `Within $${(maxDD - dd).toFixed(2)} of trailing DD breach`,
        });
      }
    } else if (ddType === "static") {
      // Static DD: simple "did balance drop below starting - maxDD?"
      const dd = startBal - state.balance;
      if (dd > maxDD) {
        state.alive = false;
        state.failure_reason = "static_drawdown";
        state.failure_date = date;
        state.failure_detail = `Static drawdown breached: $${dd.toFixed(2)} > limit $${maxDD}`;
        break;
      }
    }

    // Track close calls on daily loss
    if (maxDLL != null && intradayMin < 0) {
      const usedPct = Math.abs(intradayMin) / maxDLL;
      if (usedPct >= 0.85 && usedPct <= 1.00) {
        state.closest_calls.push({
          date, type: "daily_loss", margin: maxDLL + intradayMin,
          detail: `Used ${(usedPct * 100).toFixed(0)}% of daily loss limit ($${Math.abs(intradayMin).toFixed(2)})`,
        });
      }
    }

    // Check if profit target met & minimum days complete
    if (profitTarget != null && (state.balance - startBal) >= profitTarget) {
      if (state.days_traded >= (rules.min_trading_days || 0)) {
        state.passed = true;
        state.pass_date = date;
        break;
      }
    }
  }

  // Final consistency check (informational, not pass/fail for the eval phase)
  let consistency_warning = null;
  if (state.passed && rules.consistency_enabled) {
    const totalProfit = state.balance - startBal;
    if (totalProfit > 0) {
      const maxDay = Math.max(...Object.values(state.daily_pnl));
      const maxPct = maxDay / totalProfit;
      if (maxPct > Number(rules.consistency_max_pct)) {
        consistency_warning = {
          max_day_pct: maxPct,
          limit_pct: Number(rules.consistency_max_pct),
          detail: `Largest day was ${(maxPct * 100).toFixed(0)}% of total profit (limit: ${(rules.consistency_max_pct * 100).toFixed(0)}%)`,
        };
      }
    }
  }

  return {
    passed: state.passed,
    failed: !state.alive,   // explicitly killed by a rule breach
    incomplete: state.alive && !state.passed,  // ran out of trades, never hit target
    failure_reason: state.failure_reason,
    failure_date: state.failure_date,
    failure_detail: state.failure_detail,
    final_balance: state.balance,
    net_profit: state.balance - startBal,
    days_traded: state.days_traded,
    trading_days: state.trading_days,
    daily_pnl: state.daily_pnl,
    daily_min_intraday: state.daily_min_intraday,
    halted_days: state.halted_days,
    closest_calls: state.closest_calls,
    consistency_warning,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// ROLLING-WINDOW SURVIVAL SCORE
//
// Runs N simulations, each starting on a different day in recent history.
// This gives a probability distribution rather than a single yes/no.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compute a survival score across multiple starting points.
 *
 * @param {Array}  trades - all trades for the account
 * @param {Object} rules  - prop firm preset
 * @param {Number} windowDays - how many simulations to run (default 30)
 * @returns {Object} aggregated result
 */
export function survivalScore(trades, rules, windowDays = 30) {
  if (!Array.isArray(trades) || trades.length === 0) {
    return { score_pct: 0, passed: 0, failed: 0, total: 0, runs: [] };
  }

  const sorted = sortTrades(trades);
  // Find all unique trade dates, most recent first
  const allDates = [...new Set(sorted.map(t => String(t.trade_date || "").slice(0, 10)))].filter(Boolean).sort();
  if (allDates.length === 0) {
    return { score_pct: 0, passed: 0, failed: 0, total: 0, runs: [] };
  }

  // For each starting date offset, run a simulation using trades from that date forward
  const runs = [];
  const offsets = Math.min(windowDays, allDates.length);

  // Use the LAST `offsets` dates as potential starting points.
  // If user has < windowDays of history, we use all available.
  const startDates = allDates.slice(-offsets);

  for (const startDate of startDates) {
    // Cap each window to 30 calendar days from the start date — same as a real eval.
    // Previously this used all remaining trades which produced identical results
    // across firms when history was short (the only variable was how much P&L
    // was left, not whether the firm's rules would have killed the account).
    const evalDeadline = new Date(startDate);
    evalDeadline.setDate(evalDeadline.getDate() + (rules.eval_days || 30));
    const deadlineStr = evalDeadline.toISOString().slice(0, 10);
    const windowTrades = sorted.filter(t => {
      const d = String(t.trade_date).slice(0, 10);
      return d >= startDate && d <= deadlineStr;
    });
    const result = simulateEval(windowTrades, rules);
    runs.push({ start_date: startDate, ...result });
  }

  const passed = runs.filter(r => r.passed).length;
  const failed = runs.filter(r => r.failed).length;
  const incomplete = runs.filter(r => r.incomplete).length;

  // Failure breakdown
  const failureCounts = {};
  for (const r of runs.filter(x => x.failed && x.failure_reason)) {
    failureCounts[r.failure_reason] = (failureCounts[r.failure_reason] || 0) + 1;
  }

  // All closest-calls across all simulations, deduped
  const allCloseCalls = [];
  for (const r of runs) {
    for (const cc of (r.closest_calls || [])) {
      allCloseCalls.push({ ...cc, start_date: r.start_date });
    }
  }

  return {
    score_pct: runs.length > 0 ? (passed / runs.length) * 100 : 0,
    passed,
    failed,
    incomplete,
    total: runs.length,
    failure_counts: failureCounts,
    closest_calls: allCloseCalls,
    runs,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// FORMATTERS / HELPERS
// ─────────────────────────────────────────────────────────────────────────────

export const FAILURE_LABEL = {
  daily_loss: "Daily Loss Exceeded",
  trailing_drawdown: "Trailing Drawdown",
  static_drawdown: "Static Drawdown",
  consistency: "Consistency Rule",
};

// Export internals for testing
export const _internals = { tradeNet, sortTrades, groupByDay };

// ─────────────────────────────────────────────────────────────────────────────
// PROFILE-BASED SIMULATION
//
// Instead of the rolling-window approach, builds a 30-day synthetic eval
// from the user's actual trading pattern. Two modes:
//
//   "last30"  — uses only trades from the last 30 calendar days
//   "alltime" — uses all trades to build a statistical profile, then
//               constructs a synthetic 30-day sequence by sampling days
//               proportionally (win days, loss days, no-trade days)
//
// Returns the same shape as survivalScore so the UI stays unchanged,
// but total is always 1 — one honest simulation, one honest result.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a trade set for the given mode, then run a single clean eval simulation.
 *
 * @param {Array}  trades     - all trades for the account
 * @param {Object} rules      - prop firm preset
 * @param {string} mode       - "last30" | "alltime"
 * @returns {Object}          - same shape as survivalScore (passed, failed, incomplete, total, runs)
 */
export function simulateEvalFromProfile(trades, rules, mode = "last30") {
  if (!Array.isArray(trades) || trades.length === 0) {
    return { score_pct: 0, passed: 0, failed: 0, incomplete: 0, total: 0, runs: [], failure_counts: {}, closest_calls: [] };
  }

  const sorted = sortTrades(trades);
  const evalDays = rules.eval_days || 30;

  let simTrades;

  if (mode === "last30") {
    // Use only trades from the last 30 calendar days
    const now = new Date();
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - 30);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    simTrades = sorted.filter(t => String(t.trade_date || "").slice(0, 10) >= cutoffStr);

    // If no trades in last 30 days, fall back to most recent 30 trading days
    if (simTrades.length === 0) {
      const allDates = [...new Set(sorted.map(t => String(t.trade_date || "").slice(0, 10)))].filter(Boolean).sort();
      const recentDates = new Set(allDates.slice(-30));
      simTrades = sorted.filter(t => recentDates.has(String(t.trade_date || "").slice(0, 10)));
    }
  } else {
    // alltime: build a synthetic 30-day sequence from the full trading profile
    const byDay = groupByDay(sorted);
    const dailyPnls = [...byDay.values()].map(dayTrades =>
      dayTrades.reduce((s, t) => s + tradeNet(t), 0)
    );

    if (dailyPnls.length === 0) {
      return { score_pct: 0, passed: 0, failed: 0, incomplete: 0, total: 0, runs: [], failure_counts: {}, closest_calls: [] };
    }

    // Build synthetic trades — one synthetic "trade" per day for evalDays days,
    // sampling actual daily P&L values from history with replacement.
    // Preserves the real distribution of wins, losses, and magnitudes.
    const baseDate = new Date();
    baseDate.setDate(baseDate.getDate() - evalDays);

    simTrades = [];
    for (let i = 0; i < evalDays; i++) {
      // Sample a random day from history
      const sampledPnl = dailyPnls[Math.floor(Math.random() * dailyPnls.length)];
      if (sampledPnl === 0) continue; // skip no-trade days

      const d = new Date(baseDate);
      d.setDate(d.getDate() + i);
      // Skip weekends
      if (d.getDay() === 0 || d.getDay() === 6) continue;

      simTrades.push({
        trade_date: d.toISOString().slice(0, 10),
        entry_time: "10:00:00",
        pnl: sampledPnl,
        fees: 0,
        commissions: 0,
      });
    }
  }

  if (simTrades.length === 0) {
    return { score_pct: 0, passed: 0, failed: 0, incomplete: 0, total: 1, runs: [], failure_counts: {}, closest_calls: [],
      empty_reason: mode === "last30" ? "No trades found in the last 30 days." : "Not enough trade history to build a profile." };
  }

  const result = simulateEval(simTrades, rules);
  const runs = [{ start_date: simTrades[0]?.trade_date, ...result }];

  const failureCounts = {};
  if (result.failed && result.failure_reason) {
    failureCounts[result.failure_reason] = 1;
  }

  return {
    score_pct: result.passed ? 100 : 0,
    passed: result.passed ? 1 : 0,
    failed: result.failed ? 1 : 0,
    incomplete: result.incomplete ? 1 : 0,
    total: 1,
    failure_counts: failureCounts,
    closest_calls: result.closest_calls || [],
    runs,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// WHAT-IF SCENARIOS
//
// Re-runs survivalScore with a modifier function applied to each trade's P&L.
// The modifier receives a trade and returns a new net P&L value.
//
// Built-in presets are defined in WHAT_IF_PRESETS below.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Run a what-if scenario by applying a P&L modifier to every trade.
 *
 * @param {Array}    trades   - original trades
 * @param {Object}   rules    - prop firm preset
 * @param {Function} modifier - (trade, originalNet) => newNet
 * @returns {Object} survivalScore result with modified trades
 */
export function whatIfScore(trades, rules, modifier) {
  if (!Array.isArray(trades) || trades.length === 0) {
    return { score_pct: 0, passed: 0, failed: 0, total: 0, runs: [] };
  }
  // Build synthetic trades that override pnl while preserving all other fields
  // (trade_date, entry_time, fees, commissions, direction stay the same).
  // We set fees and commissions to 0 and put the modified net into pnl so
  // tradeNet() returns exactly what the modifier produced.
  const modifiedTrades = trades.map(t => {
    const originalNet = tradeNet(t);
    const newNet = modifier(t, originalNet);
    return { ...t, pnl: newNet, fees: 0, commissions: 0 };
  });
  return survivalScore(modifiedTrades, rules);
}

// ─────────────────────────────────────────────────────────────────────────────
// WHAT-IF PRESET DEFINITIONS
//
// Each preset describes one "what if" scenario. The UI renders these as
// buttons; each button re-runs the simulation with the given modifier.
//
// id          — unique key
// label       — button text
// description — one-line explanation shown below the result
// modifier    — (trade, originalNet) => newNet
// ─────────────────────────────────────────────────────────────────────────────

export const WHAT_IF_PRESETS = [
  {
    id: "cut_losers_20",
    label: "Cut losing days −20%",
    description: "If you reduced each losing day's P&L loss by 20% (tighter stops, smaller size, or walking away sooner).",
    modifier: (t, net) => net < 0 ? net * 0.80 : net,
  },
  {
    id: "cut_losers_50",
    label: "Cut losing days −50%",
    description: "If you cut each losing day in half — taking heat off the account on your worst sessions.",
    modifier: (t, net) => net < 0 ? net * 0.50 : net,
  },
  {
    id: "remove_worst_3",
    label: "Remove 3 worst days",
    description: "If you had sat out your 3 worst P&L days entirely — revenge trading days, news days, or days you knew you shouldn't have traded.",
    modifier: null, // special-cased in whatIfScorePreset
    special: "remove_worst_n",
    n: 3,
  },
  {
    id: "remove_worst_5",
    label: "Remove 5 worst days",
    description: "If you had skipped your 5 worst P&L days entirely.",
    modifier: null,
    special: "remove_worst_n",
    n: 5,
  },
  {
    id: "scale_winners_120",
    label: "Scale winners +20%",
    description: "If you had held winning days 20% longer or scaled up slightly on your best sessions.",
    modifier: (t, net) => net > 0 ? net * 1.20 : net,
  },
  {
    id: "add_5_avg_days",
    label: "Add 5 more trading days",
    description: "If you had 5 additional trading days at your historical average daily P&L — simulates trading more consistently.",
    modifier: null,
    special: "add_avg_days",
    n: 5,
  },
];

/**
 * Run a what-if preset scenario, handling special cases.
 *
 * @param {Array}  trades  - original trades
 * @param {Object} rules   - prop firm preset
 * @param {Object} preset  - one of WHAT_IF_PRESETS
 * @returns {Object} survivalScore result
 */
export function whatIfScorePreset(trades, rules, preset) {
  if (!Array.isArray(trades) || trades.length === 0) {
    return { score_pct: 0, passed: 0, failed: 0, total: 0, runs: [] };
  }

  if (preset.modifier) {
    return whatIfScore(trades, rules, preset.modifier);
  }

  // Special case: remove_worst_n — zero out all trades on the N worst P&L days
  if (preset.special === "remove_worst_n") {
    const byDay = groupByDay(trades);
    const dayTotals = [...byDay.entries()].map(([date, dayTrades]) => ({
      date,
      total: dayTrades.reduce((s, t) => s + tradeNet(t), 0),
    }));
    // Sort ascending — most negative days first
    const worstDays = new Set(
      dayTotals.sort((a, b) => a.total - b.total).slice(0, preset.n).map(d => d.date)
    );
    const modifiedTrades = trades.filter(t => !worstDays.has(String(t.trade_date || "").slice(0, 10)));
    return survivalScore(modifiedTrades, rules);
  }

  // Special case: add_avg_days — append synthetic days at historical avg
  if (preset.special === "add_avg_days") {
    const byDay = groupByDay(trades);
    const dailyTotals = [...byDay.values()].map(dayTrades =>
      dayTrades.reduce((s, t) => s + tradeNet(t), 0)
    );
    if (dailyTotals.length === 0) return survivalScore(trades, rules);
    const avgDayPnl = dailyTotals.reduce((s, v) => s + v, 0) / dailyTotals.length;

    // Find the last date and append N synthetic days after it
    const sortedDates = [...byDay.keys()].sort();
    const lastDate = sortedDates[sortedDates.length - 1];
    const syntheticTrades = [];
    for (let i = 1; i <= preset.n; i++) {
      // Advance by i days from last date (rough — doesn't skip weekends,
      // but the simulator groups by date so the exact value just needs to
      // be a valid YYYY-MM-DD later than lastDate)
      const d = new Date(lastDate);
      d.setDate(d.getDate() + i);
      const dateStr = d.toISOString().slice(0, 10);
      syntheticTrades.push({
        trade_date: dateStr,
        entry_time: "10:00:00",
        pnl: avgDayPnl,
        fees: 0,
        commissions: 0,
      });
    }
    return survivalScore([...trades, ...syntheticTrades], rules);
  }

  return survivalScore(trades, rules);
}

// ─────────────────────────────────────────────────────────────────────────────
// PROGRESS PROJECTION
//
// Estimates how many more trading days the user needs to pass a given eval
// at their current average daily P&L, and whether they're likely to be
// killed by drawdown before they'd hit the target.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Project how many more days are needed to pass at the current pace.
 *
 * @param {Array}  trades  - all trades for the account
 * @param {Object} rules   - prop firm preset
 * @returns {Object}
 *   {
 *     avg_daily_pnl: number,         // historical avg net P&L per trading day
 *     needed_profit: number,         // profit target amount
 *     avg_progress_pct: number,      // how far you typically get (0–100)
 *     days_to_pass: number | null,   // estimated days needed (null if avg daily ≤ 0)
 *     drawdown_risk: boolean,        // likely to blow before passing?
 *     drawdown_days: number | null,  // estimated days until drawdown breach at avg pace
 *     on_track: boolean,             // will likely pass before blowing account
 *     consistency_ok: boolean,       // best day is within consistency limit
 *     best_day_pct: number,          // best single day as % of profit target
 *   }
 */
export function projectToPass(trades, rules) {
  if (!Array.isArray(trades) || trades.length === 0) {
    return null;
  }

  const startBal      = Number(rules.starting_balance);
  const profitTarget  = rules.profit_target != null ? Number(rules.profit_target) : null;
  const maxDD         = Number(rules.max_drawdown);
  const maxDLL        = rules.max_daily_loss != null ? Number(rules.max_daily_loss) : null;

  // Compute per-day stats from actual history
  const byDay = groupByDay(trades);
  const dailyTotals = [...byDay.values()].map(dayTrades =>
    dayTrades.reduce((s, t) => s + tradeNet(t), 0)
  );
  if (dailyTotals.length === 0) return null;

  const avgDailyPnl = dailyTotals.reduce((s, v) => s + v, 0) / dailyTotals.length;
  const bestDay     = Math.max(...dailyTotals);
  const worstDay    = Math.min(...dailyTotals);

  // Average progress toward target across all runs
  const score = survivalScore(trades, rules);
  const avgProgressPct = profitTarget != null && score.runs.length > 0
    ? score.runs.reduce((s, r) => s + Math.min(100, Math.max(0, (r.net_profit / profitTarget) * 100)), 0) / score.runs.length
    : 0;

  // How many days to reach profit target at avg pace?
  let daysToPass = null;
  if (profitTarget != null && avgDailyPnl > 0) {
    // Already accounting for the average progress the simulation shows
    const remainingProfit = profitTarget * (1 - avgProgressPct / 100);
    daysToPass = Math.ceil(Math.max(remainingProfit, 0) / avgDailyPnl);
    // Add minimum trading days requirement if applicable
    if (rules.min_trading_days) {
      daysToPass = Math.max(daysToPass, rules.min_trading_days);
    }
  }

  // Drawdown risk: at avg daily P&L, how many losing days until DD breach?
  // Uses the average losing day size as the loss unit.
  const losingDays = dailyTotals.filter(v => v < 0);
  const avgLosingDay = losingDays.length > 0
    ? losingDays.reduce((s, v) => s + v, 0) / losingDays.length
    : 0;

  let drawdownDays = null;
  let drawdownRisk = false;
  if (avgLosingDay < 0 && maxDD > 0) {
    // How many consecutive avg losing days to breach max drawdown?
    drawdownDays = Math.floor(maxDD / Math.abs(avgLosingDay));
    // If it would take fewer losing days to blow the account than winning
    // days to hit the target, flag it as a drawdown risk.
    if (daysToPass != null) {
      const winRate = dailyTotals.filter(v => v > 0).length / dailyTotals.length;
      const expectedLossDays = daysToPass * (1 - winRate);
      drawdownRisk = expectedLossDays > drawdownDays * 0.7;
    }
  }

  // Consistency check: best single day as % of profit target
  const bestDayPct = profitTarget != null && profitTarget > 0
    ? (bestDay / profitTarget) * 100
    : 0;
  const consistencyLimit = rules.consistency_enabled
    ? Number(rules.consistency_max_pct) * 100
    : null;
  const consistencyOk = consistencyLimit == null || bestDayPct <= consistencyLimit;

  return {
    avg_daily_pnl: avgDailyPnl,
    avg_winning_day: dailyTotals.filter(v => v > 0).length > 0
      ? dailyTotals.filter(v => v > 0).reduce((s, v) => s + v, 0) / dailyTotals.filter(v => v > 0).length
      : 0,
    avg_losing_day: avgLosingDay,
    win_rate: dailyTotals.filter(v => v > 0).length / dailyTotals.length,
    best_day: bestDay,
    worst_day: worstDay,
    needed_profit: profitTarget,
    avg_progress_pct: avgProgressPct,
    days_to_pass: daysToPass,
    drawdown_risk: drawdownRisk,
    drawdown_days: drawdownDays,
    on_track: daysToPass != null && !drawdownRisk,
    consistency_ok: consistencyOk,
    best_day_pct: bestDayPct,
    consistency_limit: consistencyLimit,
    total_days_in_history: dailyTotals.length,
  };
}
